import { useLayoutEffect, useState } from 'react';

/**
 * Walks up the DOM from `node` to find the nearest ancestor that scrolls horizontally.
 * The table element itself grows to its content width, so the element that actually
 * scrolls (and that sticky cells are pinned against) is an ancestor.
 */
const getScrollParentX = (node: HTMLElement | null): HTMLElement | null => {
  let current = node?.parentElement ?? null;
  while (current) {
    const { overflowX } = getComputedStyle(current);
    if (overflowX === 'auto' || overflowX === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

/**
 * Reports whether the table's scroll container has room to scroll on the horizontal axis,
 * and whether it is currently scrolled all the way to its right edge.
 *
 * The right-edge signal lets consumers know when a right-stuck cell/column has been
 * scrolled into view and is resting at its natural position (so it no longer needs to
 * behave as if it were pinned).
 */
export const useCanScrollX = (): [(element: HTMLElement | null) => void, boolean, boolean] => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(true);

  useLayoutEffect(() => {
    if (!element) {
      return undefined;
    }

    // The element that actually scrolls is an ancestor; fall back to the element itself
    // in the unlikely case it is its own scroll container.
    const scroller = getScrollParentX(element) ?? element;

    // Grace buffer so that nudging slightly away from the right edge doesn't immediately
    // hide a right-stuck column that is still effectively resting in place. We use the
    // stuck column's own width: only once it has been scrolled further than its footprint
    // is it genuinely pinned over other content.
    let endThreshold = 0;
    const measureThreshold = () => {
      const stuckCell = element.querySelector<HTMLElement>('[data-stuck="right"]');
      endThreshold = stuckCell?.offsetWidth ?? 0;
    };

    const update = () => {
      const canScrollX = scroller.scrollWidth - scroller.clientWidth > 1;
      setCanScroll(canScrollX);
      const distanceToEnd = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
      setIsScrolledToEnd(!canScrollX || distanceToEnd <= endThreshold + 1);
    };

    measureThreshold();
    update();
    scroller.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      measureThreshold();
      update();
    });
    resizeObserver.observe(scroller);
    resizeObserver.observe(element);

    return () => {
      scroller.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [element]);

  return [setElement, canScroll, isScrolledToEnd];
};
